import { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  Plus,
  Trash2,
  Pencil,
  ChevronDown,
  ChevronUp,
  DollarSign,
  AlertTriangle,
  Receipt,
  FileText,
  ClipboardList,
  Package,
  Activity,
  ChevronRight,
  Ruler,
  ExternalLink,
  RefreshCw,
  Download,
} from 'lucide-react';
import {
  useProject,
  useUpdateProject,
  useProjectMaestros,
  useAssignMaestro,
  useRemoveMaestro,
  useSetProjectRate,
  useDeleteProjectRate,
  useSyncMaestroRates,
  type AssignedMaestroDTO,
} from '../../hooks/useProjects';
import { useProformas, type ProformaListDTO } from '../../hooks/useProformas';
import { useResidentes } from '../../hooks/useUsers';
import { useProjectSummary, type ActivityDTO } from '../../hooks/useProjectSummary';
import { useMaestros, type MaestroListDTO } from '../../hooks/useMaestros';
import { useRecipes, useRecipe, type RecipeListDTO } from '../../hooks/useRecipes';
import { useAnticipos, useCreateAnticipo, useUpdateAnticipo, useDeleteAnticipo, type AnticipoDTO } from '../../hooks/useAnticipos';
import { usePlanillas, useDeletePlanilla } from '../../hooks/usePlanillas';
import { useCabeceras, useCreateCabecera, useUpdateCabecera, useDeleteCabecera, type CabeceraDTO } from '../../hooks/useCabeceras';
import { usePlanillaHistorial, usePlanillaHistorialPdf } from '../../hooks/usePlanillaHistorial';
import { FileUpload, type UploadedFile } from '../../components/shared/FileUpload';
import {
  useMedidaSets,
  useCreateMedidaSet,
  useDeleteMedidaSet,
  type MedidaSetDTO,
} from '../../hooks/useMedidaSets';
import { useMedidas, useDeleteMedida } from '../../hooks/useMedidas';
import { api } from '../../lib/api-client';
import { useAuthStore } from '../../stores/auth-store';
import { toast } from '../../components/ui/Toast';
import { queryKeys } from '../../lib/query-keys';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { StatusBadge } from '../../components/ui/Badge';
import { Skeleton, SkeletonTable, SkeletonText } from '../../components/ui/Skeleton';
import { AdvanceBar } from '../../components/shared/AdvanceBar';

const TABS = ['Resumen', 'Medidas', 'Proforma', 'Planillas', 'Materiales', 'Maestros', 'Pagos', 'Liquidacion'] as const;
type Tab = (typeof TABS)[number];

// Tabs that require the proforma to be approved before they make sense
const POST_APPROVAL_TABS: Tab[] = ['Planillas', 'Materiales', 'Maestros', 'Pagos', 'Liquidacion'];
const POST_APPROVAL_STATES = ['proforma_aprobada', 'en_ejecucion', 'pre_liquidacion', 'liquidado', 'finalizado'];

function getVisibleTabs(projectEstado: string): Tab[] {
  if (POST_APPROVAL_STATES.includes(projectEstado)) {
    return [...TABS];
  }
  return TABS.filter((t) => !POST_APPROVAL_TABS.includes(t));
}

const TAB_ICONS: Record<Tab, React.ReactNode> = {
  Resumen: <Activity className="w-4 h-4 inline mr-1.5 -mt-0.5" />,
  Medidas: <Ruler className="w-4 h-4 inline mr-1.5 -mt-0.5" />,
  Proforma: <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" />,
  Planillas: <ClipboardList className="w-4 h-4 inline mr-1.5 -mt-0.5" />,
  Materiales: <Package className="w-4 h-4 inline mr-1.5 -mt-0.5" />,
  Maestros: <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" />,
  Pagos: <Receipt className="w-4 h-4 inline mr-1.5 -mt-0.5" />,
  Liquidacion: <DollarSign className="w-4 h-4 inline mr-1.5 -mt-0.5" />,
};

// ── Historial Cross-Planilla Tab (US-CAB-003, US-CAB-004, US-CAB-005) ──

const PLANILLA_ESTADOS = ['borrador', 'pendiente_compras', 'pendiente_administrativo', 'correccion_solicitada', 'revisada', 'aprobada', 'pagada'];

const TIPO_LABELS: Record<string, { label: string; color: string }> = {
  apu: { label: 'APU', color: 'bg-blue-100 text-blue-800' },
  apu_adicional: { label: 'APU Adicional', color: 'bg-violet-100 text-violet-800' },
  extra: { label: 'Extra', color: 'bg-amber-100 text-amber-800' },
  descuento: { label: 'Descuento', color: 'bg-red-100 text-red-800' },
};

const CATEGORIA_LABELS: Record<string, string> = {
  gypsum: 'Gypsum',
  pintura: 'Pintura',
  varios: 'Varios cliente',
  varios_maestro: 'Varios maestro',
  multa: 'Multa',
  herramienta: 'Herramienta',
  anticipo_maestro: 'Anticipo',
  otro: 'Otro',
  administrativo: 'Administrativo',
};

function HistorialPlanillasTab({ projectId }: { projectId: string }) {
  const { data: cabeceras = [] } = useCabeceras(projectId);
  const { data: projectMaestros = [] } = useProjectMaestros(projectId);
  const [filters, setFilters] = useState<{
    page: number; cabeceraId?: string; maestroId?: string; recipeId?: string;
    mdoMaterialId?: string; estado?: string; fechaDesde?: string; fechaHasta?: string;
    tipo?: 'apu' | 'apu_adicional' | 'extra' | 'descuento';
  }>({ page: 1 });

  const { data, isLoading } = usePlanillaHistorial(projectId, {
    ...filters,
    includeResumen: 'true',
    limit: 50,
  });

  const downloadPdf = usePlanillaHistorialPdf(projectId);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 50);

  // Derive unique APUs and MDOs from project maestros' rates (no extra API call)
  const apuOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of projectMaestros) {
      for (const r of m.project_rates) {
        if (!map.has(r.recipe_id)) map.set(r.recipe_id, r.recipe_nombre);
      }
    }
    return Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
  }, [projectMaestros]);

  const mdoOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of projectMaestros) {
      for (const r of m.project_rates) {
        for (const mdo of r.mdo_items) {
          if (!map.has(mdo.material_id)) map.set(mdo.material_id, mdo.material_nombre);
        }
      }
    }
    return Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
  }, [projectMaestros]);

  const updateFilter = (key: string, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(n);

  const hasActiveFilters = !!(filters.cabeceraId || filters.maestroId || filters.recipeId ||
    filters.mdoMaterialId || filters.estado || filters.fechaDesde || filters.fechaHasta || filters.tipo);

  const handleDownloadPdf = () => {
    const cabeceraLabel = filters.cabeceraId
      ? cabeceras.find((c) => c.id === filters.cabeceraId)?.nombre
      : undefined;
    const maestroLabel = filters.maestroId
      ? projectMaestros.find((m) => m.maestro_id === filters.maestroId)?.nombre
      : undefined;
    const apuLabel = filters.recipeId
      ? apuOptions.find((r) => r.id === filters.recipeId)?.nombre
      : undefined;
    const mdoLabel = filters.mdoMaterialId
      ? mdoOptions.find((m) => m.id === filters.mdoMaterialId)?.nombre
      : undefined;

    downloadPdf.mutate({
      ...filters,
      cabeceraLabel,
      maestroLabel,
      apuLabel,
      mdoLabel,
    });
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {cabeceras.length > 0 && (
          <select
            className="bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800"
            value={filters.cabeceraId ?? ''}
            onChange={(e) => updateFilter('cabeceraId', e.target.value)}
          >
            <option value="">Todas las cabeceras</option>
            {cabeceras.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        )}
        {projectMaestros.length > 0 && (
          <select
            className="bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800"
            value={filters.maestroId ?? ''}
            onChange={(e) => updateFilter('maestroId', e.target.value)}
          >
            <option value="">Todos los maestros</option>
            {projectMaestros.map((m) => (
              <option key={m.maestro_id} value={m.maestro_id}>{m.nombre}</option>
            ))}
          </select>
        )}
        <select
          className="bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800"
          value={filters.tipo ?? ''}
          onChange={(e) => updateFilter('tipo', e.target.value)}
        >
          <option value="">Todos los tipos</option>
          <option value="apu">APU</option>
          <option value="apu_adicional">APU Adicional</option>
          <option value="extra">Extras</option>
          <option value="descuento">Descuentos</option>
        </select>
        {(apuOptions.length > 0 && (!filters.tipo || filters.tipo === 'apu' || filters.tipo === 'apu_adicional')) && (
          <select
            className="bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800"
            value={filters.recipeId ?? ''}
            onChange={(e) => updateFilter('recipeId', e.target.value)}
          >
            <option value="">Todos los APUs</option>
            {apuOptions.map((r) => (
              <option key={r.id} value={r.id}>{r.nombre}</option>
            ))}
          </select>
        )}
        {(mdoOptions.length > 0 && (!filters.tipo || filters.tipo === 'apu' || filters.tipo === 'apu_adicional')) && (
          <select
            className="bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800"
            value={filters.mdoMaterialId ?? ''}
            onChange={(e) => updateFilter('mdoMaterialId', e.target.value)}
          >
            <option value="">Todos los MDO</option>
            {mdoOptions.map((m) => (
              <option key={m.id} value={m.id}>{m.nombre}</option>
            ))}
          </select>
        )}
        <select
          className="bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800"
          value={filters.estado ?? ''}
          onChange={(e) => updateFilter('estado', e.target.value)}
        >
          <option value="">Todos los estados</option>
          {PLANILLA_ESTADOS.map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-xs text-gray-500">
          Desde
          <input
            type="date"
            className="bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800"
            value={filters.fechaDesde ?? ''}
            onChange={(e) => updateFilter('fechaDesde', e.target.value)}
          />
        </label>
        <label className="flex items-center gap-1 text-xs text-gray-500">
          Hasta
          <input
            type="date"
            className="bg-white border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800"
            value={filters.fechaHasta ?? ''}
            onChange={(e) => updateFilter('fechaHasta', e.target.value)}
          />
        </label>
        {hasActiveFilters && (
          <Button size="sm" variant="ghost" onClick={() => setFilters({ page: 1 })}>
            Limpiar filtros
          </Button>
        )}
      </div>

      {/* Results count + PDF download */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-400">{total} registros</div>
        <Button
          size="sm"
          variant="ghost"
          disabled={total === 0 || downloadPdf.isPending}
          onClick={handleDownloadPdf}
          className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900"
        >
          <Download className="w-3.5 h-3.5" />
          {downloadPdf.isPending ? 'Generando...' : 'Descargar PDF'}
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-sm text-gray-400">Cargando historial...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">
          {!hasActiveFilters
            ? 'Aún no hay planillas registradas para este proyecto.'
            : filters.cabeceraId
            ? 'No hay registros para esta cabecera.'
            : 'No hay registros con los filtros seleccionados.'}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Estado', 'Fecha', 'Cabecera', 'Maestro', 'Tipo', 'Concepto', 'MDO', 'Cant.', 'Monto'].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-gray-600 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => {
                  const tipoInfo = TIPO_LABELS[item.tipo] ?? { label: item.tipo, color: 'bg-gray-100 text-gray-700' };
                  return (
                    <tr key={`${item.planilla_id}-${item.tipo}-${i}`} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <StatusBadge entity="planilla" status={item.planilla_estado} />
                      </td>
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap text-xs">
                        {new Date(item.planilla_created_at).toLocaleDateString('es-EC')}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {item.cabecera_nombre ?? <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{item.maestro_nombre}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${tipoInfo.color}`}>
                          {tipoInfo.label}
                          {item.categoria && (
                            <span className="ml-1 font-normal">
                              · {CATEGORIA_LABELS[item.categoria] ?? item.categoria}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-800 max-w-[200px]">
                        <span className="block truncate" title={item.concepto}>{item.concepto}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {item.mdo_material_nombre ?? <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                        {item.cantidad != null ? `${item.cantidad}${item.unidad ? ` ${item.unidad}` : ''}` : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900 whitespace-nowrap">
                        {item.tipo === 'descuento'
                          ? <span className="text-red-600">-{formatCurrency(item.monto)}</span>
                          : formatCurrency(item.monto)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td colSpan={8} className="px-3 py-2 text-right text-xs text-gray-600">
                    Subtotal (página):
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-gray-900">
                    {formatCurrency(items.reduce((acc, item) =>
                      acc + (item.tipo === 'descuento' ? -item.monto : item.monto), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {items.map((item, i) => {
              const tipoInfo = TIPO_LABELS[item.tipo] ?? { label: item.tipo, color: 'bg-gray-100 text-gray-700' };
              return (
                <div key={`${item.planilla_id}-${item.tipo}-${i}`} className="bg-white/70 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <StatusBadge entity="planilla" status={item.planilla_estado} />
                    <span className="text-xs text-gray-500">
                      {new Date(item.planilla_created_at).toLocaleDateString('es-EC')}
                    </span>
                  </div>
                  {item.cabecera_nombre && (
                    <div className="text-xs text-sky-700 font-medium mb-1">{item.cabecera_nombre}</div>
                  )}
                  <div className="flex items-start gap-1.5 mb-1">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium mt-0.5 ${tipoInfo.color}`}>
                      {tipoInfo.label}
                    </span>
                    <span className="text-sm text-gray-800">{item.concepto}</span>
                  </div>
                  {item.categoria && (
                    <div className="text-xs text-gray-500">{CATEGORIA_LABELS[item.categoria] ?? item.categoria}</div>
                  )}
                  {item.mdo_material_nombre && (
                    <div className="text-xs text-gray-500 italic">MDO: {item.mdo_material_nombre}</div>
                  )}
                  <div className="text-xs text-gray-600">{item.maestro_nombre}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-600">
                      {item.cantidad != null ? `${item.cantidad}${item.unidad ? ` ${item.unidad}` : ''}` : ''}
                    </span>
                    <span className={`text-sm font-bold ${item.tipo === 'descuento' ? 'text-red-600' : 'text-gray-900'}`}>
                      {item.tipo === 'descuento' ? '-' : ''}{formatCurrency(item.monto)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <Button
                size="sm"
                variant="ghost"
                disabled={filters.page <= 1}
                onClick={() => setFilters((p) => ({ ...p, page: p.page - 1 }))}
              >
                ← Anterior
              </Button>
              <span className="text-xs text-gray-400">
                Página {filters.page} de {totalPages}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={filters.page >= totalPages}
                onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
              >
                Siguiente →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Cabeceras Manager ────────────────────────────────────────────────

function CabecerasManager({ projectId, canManage }: { projectId: string; canManage: boolean }) {
  const { data: cabeceras = [], isLoading } = useCabeceras(projectId);
  const createMutation = useCreateCabecera(projectId);
  const updateMutation = useUpdateCabecera(projectId);
  const deleteMutation = useDeleteCabecera(projectId);

  const [showCreate, setShowCreate] = useState(false);
  const [newNombre, setNewNombre] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNombre, setEditNombre] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CabeceraDTO | null>(null);

  const handleCreate = () => {
    if (!newNombre.trim()) return;
    createMutation.mutate({ nombre: newNombre.trim() }, {
      onSuccess: () => { setShowCreate(false); setNewNombre(''); },
    });
  };

  const handleUpdate = (id: string) => {
    if (!editNombre.trim()) return;
    updateMutation.mutate({ cabeceraId: id, nombre: editNombre.trim() }, {
      onSuccess: () => setEditingId(null),
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  };

  if (isLoading) return <div className="text-sm text-gray-600">Cargando cabeceras...</div>;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">Cabeceras del Proyecto</h3>
        {canManage && (
          <Button size="sm" variant="secondary" onClick={() => setShowCreate(true)}>
            + Nueva Cabecera
          </Button>
        )}
      </div>

      {cabeceras.length === 0 ? (
        <p className="text-xs text-gray-500">Sin cabeceras definidas. Añade unidades de vivienda para clasificar el avance por departamento.</p>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 text-gray-600 font-medium">Nombre</th>
                <th className="text-right px-3 py-2 text-gray-600 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cabeceras.map((cab) => (
                <tr key={cab.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-gray-800">
                    {editingId === cab.id ? (
                      <input
                        className="bg-white border border-gray-300 rounded px-2 py-1 text-sm text-gray-800 w-full"
                        value={editNombre}
                        onChange={(e) => setEditNombre(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUpdate(cab.id)}
                        autoFocus
                        maxLength={50}
                      />
                    ) : (
                      cab.nombre
                    )}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    {canManage && editingId === cab.id ? (
                      <>
                        <Button size="sm" variant="primary" onClick={() => handleUpdate(cab.id)} isLoading={updateMutation.isPending}>Guardar</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancelar</Button>
                      </>
                    ) : canManage ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => { setEditingId(cab.id); setEditNombre(cab.nombre); }}>Editar</Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(cab)} className="text-red-400 hover:text-red-300">✕</Button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Create modal */}
      <Modal isOpen={showCreate} onClose={() => { setShowCreate(false); setNewNombre(''); }} title="Nueva Cabecera" size="sm">
        <div className="space-y-4">
          <Input
            label="Nombre (ej. DEP 101, CASA 3, Área Común)"
            value={newNombre}
            onChange={(e) => setNewNombre(e.target.value)}
            maxLength={50}
            placeholder="DEP 101"
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button variant="primary" onClick={handleCreate} isLoading={createMutation.isPending} disabled={!newNombre.trim()}>Crear</Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Eliminar Cabecera" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-700">¿Eliminar la cabecera <strong>{deleteTarget?.nombre}</strong>? Solo es posible si no tiene registros de planilla asociados.</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="primary" onClick={handleDelete} isLoading={deleteMutation.isPending} className="bg-red-600 hover:bg-red-500">Eliminar</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const canEdit = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');
  const canManageMaestros = ['administrativo', 'gerente', 'operaciones', 'residente'].includes(user?.role ?? '');
  const canManageCabeceras = ['administrativo', 'gerente', 'operaciones', 'residente'].includes(user?.role ?? '');
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const tabParam = searchParams.get('tab');
    return TABS.includes(tabParam as Tab) ? (tabParam as Tab) : 'Resumen';
  });
  const [showHistorial, setShowHistorial] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const updateProjectName = useUpdateProject();

  const { data: project, isLoading: projectLoading } = useProject(id ?? '');
  const { data: summary, isLoading: summaryLoading } = useProjectSummary(id ?? '');

  const visibleTabs = project ? getVisibleTabs(project.estado) : (['Resumen', 'Medidas', 'Proforma'] as Tab[]);
  const safeActiveTab: Tab = visibleTabs.includes(activeTab) ? activeTab : 'Resumen';

  if (projectLoading) {
    return (
      <div className="space-y-6 fade-up">
        <SkeletonTable rows={3} cols={2} />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="space-y-6 fade-up">
        <p className="text-gray-500">Proyecto no encontrado</p>
      </div>
    );
  }

  const handleSaveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === project.nombre) { setEditingName(false); return; }
    await updateProjectName.mutateAsync({ id: project.id, data: { nombre: trimmed } });
    setEditingName(false);
  };

  return (
    <div className="space-y-6 fade-up">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/proyectos" className="text-gray-400 hover:text-brand-dark">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1 min-w-0">
          {editingName ? (
            <div className="flex items-center gap-2 mb-1">
              <input
                className="text-2xl font-bold text-brand-dark bg-white border-b-2 border-brand-blue focus:outline-none flex-1 min-w-0"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') setEditingName(false);
                }}
                autoFocus
              />
              <Button size="sm" variant="primary" onClick={handleSaveName} disabled={updateProjectName.isPending}>
                {updateProjectName.isPending ? 'Guardando...' : 'Guardar'}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setEditingName(false)}>Cancelar</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mb-0.5">
              <h1 className="text-2xl font-bold text-brand-dark truncate">{project.nombre}</h1>
              {canEdit && (
                <button
                  onClick={() => { setNameValue(project.nombre); setEditingName(true); }}
                  className="text-gray-400 hover:text-brand-blue flex-shrink-0 mt-0.5"
                  title="Editar nombre"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
          <p className="text-sm text-gray-500">
            {project.codigo} &middot; {project.client?.nombre}
          </p>
        </div>
        <StatusBadge entity="project" status={project.estado} />
      </div>

      {/* Tabs — horizontal scrollable on mobile */}
      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        <div className="flex gap-1 border-b border-gray-200 min-w-max">
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                if (tab === 'Liquidacion') {
                  navigate(`/liquidacion?projectId=${id}`);
                  return;
                }
                setActiveTab(tab);
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                safeActiveTab === tab
                  ? 'border-[#3c98ff] text-[#3c98ff]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {TAB_ICONS[tab]}
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {safeActiveTab === 'Resumen' && (
        <ResumenTab
          project={project}
          summary={summary ?? null}
          summaryLoading={summaryLoading}
          navigate={navigate}
          onTabChange={setActiveTab}
          canEdit={canEdit}
        />
      )}

      {safeActiveTab === 'Medidas' && (
        <MedidasTab projectId={id!} navigate={navigate} medidasNotas={project?.medidas_notas ?? null} />
      )}

      {safeActiveTab === 'Proforma' && (
        <ProformasTab projectId={id!} navigate={navigate} canEdit={canEdit} />
      )}

      {safeActiveTab === 'Planillas' && (
        <div className="space-y-4">
          <CabecerasManager projectId={id!} canManage={canManageCabeceras} />
          <PlanillasInlineTab projectId={id ?? ''} />
          <div className="mt-4">
            <button
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              onClick={() => setShowHistorial((v) => !v)}
            >
              <ChevronRight className={`w-4 h-4 transition-transform ${showHistorial ? 'rotate-90' : ''}`} />
              Historial cross-planilla
            </button>
            {showHistorial && (
              <div className="mt-3">
                <HistorialPlanillasTab projectId={id!} />
              </div>
            )}
          </div>
        </div>
      )}

      {safeActiveTab === 'Materiales' && (
        <div className="space-y-4">
          <PlaceholderTab
            title="Materiales y Pedidos"
            message={`${summary?.pedidos_count ?? 0} pedido(s) registrados`}
            actionLabel="Ver Pedidos"
            onAction={() => navigate(`/proyectos/${id}/pedidos`)}
            canEdit={true}
          />
          <div className="flex justify-center">
            <Button variant="primary" onClick={() => navigate(`/proyectos/${id}/pedidos/nuevo`)}>
              Nuevo Pedido de Materiales
            </Button>
          </div>
        </div>
      )}

      {safeActiveTab === 'Maestros' && (
        <MaestrosTab projectId={id ?? ''} canEdit={canManageMaestros} />
      )}

      {safeActiveTab === 'Pagos' && (
        <PagosTab projectId={id ?? ''} canEdit={canEdit} />
      )}

      {safeActiveTab === 'Liquidacion' && (
        <LiquidacionTab projectId={id ?? ''} projectEstado={project.estado} navigate={navigate} />
      )}
    </div>
  );
}

// ── Resumen Tab ─────────────────────────────────────────────────────

function ChangeResidenteModal({
  projectId,
  currentResidenteId,
  onClose,
}: {
  projectId: string;
  currentResidenteId: string;
  onClose: () => void;
}) {
  const [selectedId, setSelectedId] = useState(currentResidenteId);
  const { data: residentes = [], isLoading } = useResidentes();
  const updateProject = useUpdateProject();

  const handleSave = async () => {
    if (selectedId === currentResidenteId) { onClose(); return; }
    await updateProject.mutateAsync({ id: projectId, data: { residenteId: selectedId } });
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} title="Reasignar Residente" size="sm">
      <div className="space-y-4">
        {isLoading ? (
          <div className="h-10 bg-gray-100 rounded animate-pulse" />
        ) : (
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            {residentes.map((r) => (
              <option key={r.id} value={r.id}>{r.nombre}</option>
            ))}
          </select>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={updateProject.isPending || isLoading}
          >
            {updateProject.isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ResumenTab({
  project,
  summary,
  summaryLoading,
  navigate,
  onTabChange,
  canEdit,
}: {
  project: { id: string; codigo: string; estado: string; direccion_obra: string; client?: { nombre: string }; residente?: { id: string; nombre: string } };
  summary: import('../../hooks/useProjectSummary').ProjectSummaryDTO | null;
  summaryLoading: boolean;
  navigate: (path: string) => void;
  onTabChange: (tab: Tab) => void;
  canEdit: boolean;
}) {
  const [showChangeResidente, setShowChangeResidente] = useState(false);
  const { user: currentUser } = useAuthStore();

  return (
    <div className="space-y-4">

      {showChangeResidente && project.residente && (
        <ChangeResidenteModal
          projectId={project.id}
          currentResidenteId={project.residente.id}
          onClose={() => setShowChangeResidente(false)}
        />
      )}

      {/* Project info card */}
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Codigo:</span>{' '}
            <span className="font-medium">{project.codigo}</span>
          </div>
          <div>
            <span className="text-gray-500">Estado:</span>{' '}
            <StatusBadge entity="project" status={project.estado} />
          </div>
          <div>
            <span className="text-gray-500">Cliente:</span>{' '}
            <span className="font-medium">{project.client?.nombre}</span>
          </div>
          <div className="flex items-center gap-2">
            <div>
              <span className="text-gray-500">Residente:</span>{' '}
              <span className="font-medium">{project.residente?.nombre ?? '—'}</span>
            </div>
            {canEdit && project.residente && (
              <button
                onClick={() => setShowChangeResidente(true)}
                className="text-xs text-brand-blue hover:underline ml-1"
              >
                Cambiar
              </button>
            )}
          </div>
          <div className="sm:col-span-2">
            <span className="text-gray-500">Direccion:</span>{' '}
            <span className="font-medium">{project.direccion_obra}</span>
          </div>
        </div>
      </Card>

      {/* AdvanceBar — payments vs proforma */}
      {summary && summary.total_proforma > 0 && (
        <Card>
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-brand-dark">Avance Financiero</h3>
            <AdvanceBar consumed={summary.pagos_total} total={summary.total_proforma} mode="progress" />
          </div>
        </Card>
      )}

      {/* Next Actions — state-dependent CTAs */}
      {summaryLoading && <SkeletonText lines={2} />}
      {!summaryLoading && summary && summary.next_actions.length > 0 && (
        <Card>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-brand-dark">Siguiente paso</h3>
            <div className="flex flex-wrap gap-2">
              {summary.next_actions.filter((action) =>
                currentUser?.role !== 'residente' || !action.path.includes('proforma'),
              ).map((action) => {
                // Map self-referencing paths to tab switches
                const TAB_MAP: Record<string, Tab> = {
                  'Asignar Maestros': 'Maestros',
                  'Registrar Pago': 'Pagos',
                  'Ver Medidas': 'Medidas',
                };
                const targetTab = TAB_MAP[action.label];
                const isSelfNav = action.path === `/proyectos/${project.id}`;

                return (
                  <Button
                    key={action.label}
                    variant={action.variant === 'primary' ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => {
                      if (isSelfNav && targetTab) {
                        onTabChange(targetTab);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      } else {
                        navigate(action.path);
                      }
                    }}
                  >
                    {action.label}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* Summary counters */}
      {!summaryLoading && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MiniCounter
            label="Proforma"
            value={summary.has_proforma ? (summary.proforma_estado ?? 'creada') : '—'}
            isStatus
          />
          <MiniCounter label="Planillas" value={summary.planillas_count} />
          <MiniCounter label="Pedidos" value={summary.pedidos_count} />
          <MiniCounter
            label="Pagos"
            value={summary.pagos_total > 0 ? `$${summary.pagos_total.toFixed(0)}` : '—'}
          />
        </div>
      )}

      {/* Recent activity feed */}
      {summaryLoading && <SkeletonTable rows={3} cols={3} />}
      {!summaryLoading && summary && (
        <Card>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-brand-dark flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-400" />
              Actividad reciente
            </h3>
            {summary.recent_activity.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">Sin actividad registrada</p>
            ) : (
              <div className="space-y-2">
                {summary.recent_activity.map((a) => (
                  <ActivityRow key={a.id} activity={a} />
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Mini Counter ────────────────────────────────────────────────────

function MiniCounter({
  label,
  value,
  isStatus,
}: {
  label: string;
  value: number | string;
  isStatus?: boolean;
}) {
  return (
    <Card className="!p-3">
      <p className="text-xs text-gray-500">{label}</p>
      {isStatus ? (
        <p className="text-sm font-semibold text-brand-dark mt-0.5 capitalize">{value}</p>
      ) : (
        <p className="text-xl font-bold text-brand-dark mt-0.5">{value}</p>
      )}
    </Card>
  );
}

// ── Activity Row ────────────────────────────────────────────────────

function ActivityRow({ activity }: { activity: ActivityDTO }) {
  const timeAgo = getTimeAgo(activity.created_at);

  return (
    <div className="flex items-start gap-3 py-1.5">
      <div className="w-1.5 h-1.5 rounded-full bg-[#3c98ff] mt-2 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-700">
          <span className="font-medium">{activity.actor_nombre ?? 'Sistema'}</span>{' '}
          {activity.description}
        </p>
        <p className="text-xs text-gray-400">{timeAgo}</p>
      </div>
    </div>
  );
}

function getTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'hace un momento';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `hace ${days}d`;
  return date.toLocaleDateString('es-EC', { day: 'numeric', month: 'short' });
}

// ── Placeholder Tab ─────────────────────────────────────────────────

// ── Planillas Inline Tab ─────────────────────────────────────────────

const ESTADO_COLORS: Record<string, string> = {
  borrador: 'bg-gray-100 text-gray-700',
  pendiente_compras: 'bg-yellow-100 text-yellow-800',
  pendiente_administrativo: 'bg-blue-100 text-blue-800',
  correccion_solicitada: 'bg-red-100 text-red-800',
  revisada: 'bg-purple-100 text-purple-800',
  aprobada: 'bg-green-100 text-green-800',
  pagada: 'bg-emerald-100 text-emerald-800',
};

const ESTADO_LABELS: Record<string, string> = {
  borrador: 'Borrador',
  pendiente_compras: 'Pend. Compras',
  pendiente_administrativo: 'Pend. Administrativo',
  correccion_solicitada: 'Corrección',
  revisada: 'Revisada',
  aprobada: 'Aprobada',
  pagada: 'Pagada',
};

function PlanillasInlineTab({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data, isLoading } = usePlanillas(projectId);
  const deletePlanilla = useDeletePlanilla();
  const planillas = data?.data ?? [];
  const canDeleteBorrador = ['residente', 'administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-brand-dark">Planillas</h2>
        <Button variant="primary" size="sm" onClick={() => navigate(`/proyectos/${projectId}/planillas/nueva`)}>
          <Plus className="w-4 h-4 mr-1" /> Nueva Planilla
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : planillas.length === 0 ? (
        <Card>
          <p className="text-center text-sm text-gray-400 py-6">No hay planillas registradas</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {planillas.map((p) => (
            <Card key={p.id} className="p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/proyectos/${projectId}/planillas/${p.id}`)}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    Planilla N°{p.numero}
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(p.fecha_desde).toLocaleDateString('es-EC')} — {new Date(p.fecha_hasta).toLocaleDateString('es-EC')}
                    {' · '}{p.created_by.nombre}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ESTADO_COLORS[p.estado] ?? 'bg-gray-100 text-gray-700'}`}>
                    {ESTADO_LABELS[p.estado] ?? p.estado}
                  </span>
                  <span className="text-xs font-semibold text-gray-600">${p.total_neto?.toFixed(2) ?? '0.00'}</span>
                  {canDeleteBorrador && p.estado === 'borrador' && (
                    <button
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Eliminar planilla"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (window.confirm('¿Eliminar esta planilla? Esta acción no se puede deshacer.')) {
                          await deletePlanilla.mutateAsync(p.id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function PlaceholderTab({
  title,
  message,
  actionLabel,
  onAction,
  canEdit,
}: {
  title: string;
  message: string;
  actionLabel: string;
  onAction: () => void;
  canEdit: boolean;
}) {
  return (
    <Card>
      <div className="py-8 text-center space-y-3">
        <h3 className="text-lg font-semibold text-brand-dark">{title}</h3>
        <p className="text-sm text-gray-500">{message}</p>
        {canEdit && (
          <Button variant="secondary" size="sm" onClick={onAction}>
            {actionLabel}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </Card>
  );
}

// ── Pagos Tab ───────────────────────────────────────────────────────

function PagosTab({
  projectId,
  canEdit,
}: {
  projectId: string;
  canEdit: boolean;
}) {
  const { data: pagosList, isLoading } = useAnticipos(projectId);
  const [showCreate, setShowCreate] = useState(false);
  const [editingPago, setEditingPago] = useState<AnticipoDTO | null>(null);
  const [deletingPago, setDeletingPago] = useState<AnticipoDTO | null>(null);
  const deleteMutation = useDeleteAnticipo();

  const handleDelete = async () => {
    if (!deletingPago) return;
    await deleteMutation.mutateAsync({ anticipoId: deletingPago.id, projectId });
    setDeletingPago(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-brand-dark">
          Registro de Pagos
          {pagosList && <span className="text-gray-400 ml-2">({pagosList.length})</span>}
        </h2>
        {canEdit && (
          <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Registrar Pago
          </Button>
        )}
      </div>

      {isLoading && <SkeletonTable rows={2} cols={4} />}

      {!isLoading && (pagosList?.length ?? 0) === 0 && (
        <Card>
          <div className="text-center py-8">
            <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No hay pagos registrados</p>
            <p className="text-xs text-gray-400 mt-1">
              Registra los pagos recibidos del cliente para este proyecto.
            </p>
          </div>
        </Card>
      )}

      {/* Pago cards */}
      {pagosList?.map((p) => (
        <Card key={p.id}>
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0">
              <p className="font-semibold text-brand-dark text-lg">
                ${p.monto.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">
                Fecha: {p.fecha_recepcion}
              </p>
              {p.notas && (
                <p className="text-xs text-gray-400">{p.notas}</p>
              )}
              {p.comprobante_s3_key && (
                <ComprobanteLink s3Key={p.comprobante_s3_key} />
              )}
            </div>
            {canEdit && (
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditingPago(p)}
                >
                  Editar
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setDeletingPago(p)}
                  className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
        </Card>
      ))}

      {/* Total */}
      {(pagosList?.length ?? 0) > 0 && (
        <Card>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Total pagos recibidos</span>
            <span className="text-lg font-bold text-brand-dark">
              ${pagosList?.reduce((sum, p) => sum + p.monto, 0).toFixed(2)}
            </span>
          </div>
        </Card>
      )}

      {showCreate && (
        <PagoFormModal
          projectId={projectId}
          onClose={() => setShowCreate(false)}
        />
      )}

      {editingPago && (
        <PagoFormModal
          projectId={projectId}
          editPago={editingPago}
          onClose={() => setEditingPago(null)}
        />
      )}

      {deletingPago && (
        <Modal isOpen title="Eliminar Pago" onClose={() => setDeletingPago(null)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              ¿Confirmas que deseas eliminar el pago de{' '}
              <strong>${deletingPago.monto.toFixed(2)}</strong> del{' '}
              {deletingPago.fecha_recepcion}? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" size="sm" onClick={() => setDeletingPago(null)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleDelete}
                isLoading={deleteMutation.isPending}
                className="bg-red-600 hover:bg-red-700 border-red-600"
              >
                Eliminar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Comprobante link ────────────────────────────────────────────────

function ComprobanteLink({ s3Key }: { s3Key: string }) {
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await api.get<{ data: { download_url: string } }>(
        '/uploads/download',
        { params: { key: s3Key } },
      );
      window.open(res.data.data.download_url, '_blank');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleOpen}
      disabled={loading}
      className="text-xs text-[#3c98ff] hover:underline flex items-center gap-1 disabled:opacity-50"
    >
      <ExternalLink className="w-3 h-3" />
      {loading ? 'Abriendo...' : 'Ver comprobante'}
    </button>
  );
}

// ── Pago Form Modal (Create + Edit) ────────────────────────────────

function PagoFormModal({
  projectId,
  editPago,
  onClose,
}: {
  projectId: string;
  editPago?: AnticipoDTO;
  onClose: () => void;
}) {
  const createMutation = useCreateAnticipo();
  const updateMutation = useUpdateAnticipo();

  const isEdit = !!editPago;
  const [monto, setMonto] = useState(editPago ? String(editPago.monto) : '');
  const [fecha, setFecha] = useState(editPago?.fecha_recepcion ?? new Date().toISOString().split('T')[0]);
  const [notas, setNotas] = useState(editPago?.notas ?? '');
  const [comprobanteKey, setComprobanteKey] = useState<string | null>(editPago?.comprobante_s3_key ?? null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const handleFilesChange = (files: UploadedFile[]) => {
    setUploadedFiles(files);
    if (files.length > 0) {
      setComprobanteKey(files[0].s3_key);
    }
  };

  const handleSubmit = async () => {
    if (!monto || !fecha) return;
    if (isEdit) {
      await updateMutation.mutateAsync({
        anticipoId: editPago.id,
        projectId,
        data: {
          monto: Number(monto),
          fechaRecepcion: fecha,
          notas: notas || null,
          comprobanteS3Key: comprobanteKey,
        },
      });
    } else {
      await createMutation.mutateAsync({
        projectId,
        data: {
          monto: Number(monto),
          fechaRecepcion: fecha,
          notas: notas || null,
          comprobanteS3Key: comprobanteKey,
        },
      });
    }
    onClose();
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const hasUploadInProgress = uploadedFiles.some((f) => !f.s3_key);

  return (
    <Modal isOpen title={isEdit ? 'Editar Pago' : 'Registrar Pago'} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monto (USD)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-sm text-gray-400">$</span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="0.00"
              className="w-full rounded-lg border border-gray-200 bg-white pl-7 pr-3 py-2 text-sm focus:border-[#3c98ff] focus:outline-none focus:ring-1 focus:ring-[#3c98ff]"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha de recepción
          </label>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#3c98ff] focus:outline-none focus:ring-1 focus:ring-[#3c98ff]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notas <span className="text-gray-400">(opcional)</span>
          </label>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Ej: Transferencia Banco Pichincha, referencia TRF-2026-001"
            maxLength={200}
            rows={2}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#3c98ff] focus:outline-none focus:ring-1 focus:ring-[#3c98ff] resize-none"
          />
          <p className="text-xs text-gray-400 text-right">{notas.length}/200</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Comprobante <span className="text-gray-400">(foto o PDF, opcional)</span>
          </label>
          {comprobanteKey && !uploadedFiles.length ? (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-3">
              <span className="text-xs text-green-600 flex-1">✓ Comprobante adjunto</span>
              <button
                onClick={() => setComprobanteKey(null)}
                className="text-xs text-red-500 hover:underline"
              >
                Quitar
              </button>
            </div>
          ) : (
            <FileUpload
              context="comprobante"
              onFilesChange={handleFilesChange}
              maxFiles={1}
              label="Comprobante"
              helpText="Foto o PDF del comprobante de pago"
            />
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            isLoading={isPending || hasUploadInProgress}
            disabled={!monto || !fecha}
          >
            {isEdit ? 'Guardar cambios' : 'Registrar pago'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Proformas Tab ────────────────────────────────────────────────────

function ProformasTab({ projectId, navigate, canEdit }: { projectId: string; navigate: (path: string) => void; canEdit: boolean }) {
  const { data, isLoading } = useProformas({ projectId, limit: 50 });
  const proformas = data?.data ?? [];

  if (isLoading) {
    return <Card className="p-4"><SkeletonTable rows={3} cols={5} /></Card>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{proformas.length} proforma(s) para este proyecto</p>
        {canEdit && (
          <p className="text-xs text-gray-400">Crear proformas desde la pestaña Medidas</p>
        )}
      </div>

      {proformas.length === 0 ? (
        <Card className="p-6 text-center">
          <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-600">No hay proformas para este proyecto</p>
          {canEdit && (
            <p className="text-xs text-gray-400 mt-2">Usa el botón &quot;+ Proforma&quot; en cada Registro de Medidas</p>
          )}
        </Card>
      ) : (
        <Card>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">N°</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Set de Medidas</th>
                  <th className="px-4 py-3 text-right">Ítems</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Fecha</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {proformas.map((p: ProformaListDTO) => (
                  <tr
                    key={p.id}
                    className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/proformas/${p.id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-brand-dark">#{p.numero}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium
                        ${p.estado === 'aprobada' ? 'bg-green-100 text-green-700' :
                          p.estado === 'enviada' ? 'bg-blue-100 text-blue-700' :
                          p.estado === 'rechazada' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'}`}>
                        {p.estado}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {p.medida_set_nombre ?? <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{p.totals.total_items}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-800">
                      ${p.totals.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{new Date(p.created_at).toLocaleDateString('es-EC')}</td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-gray-400 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-gray-100">
            {proformas.map((p: ProformaListDTO) => (
              <div
                key={p.id}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/proformas/${p.id}`)}
              >
                <div>
                  <p className="text-sm font-medium text-brand-dark">Proforma #{p.numero}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {p.totals.total_items} ítem(s) · {new Date(p.created_at).toLocaleDateString('es-EC')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium
                    ${p.estado === 'aprobada' ? 'bg-green-100 text-green-700' :
                      p.estado === 'enviada' ? 'bg-blue-100 text-blue-700' :
                      p.estado === 'rechazada' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-600'}`}>
                    {p.estado}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Medidas Tab ──────────────────────────────────────────────────────

function MedidasTab({ projectId, navigate }: { projectId: string; navigate: (path: string) => void; medidasNotas?: string | null }) {
  const queryClient = useQueryClient();
  const { data: sets, isLoading: setsLoading } = useMedidaSets(projectId);
  const { data: orphanMedidas, isLoading: orphanLoading } = useMedidas(projectId, undefined, true);
  const createSet = useCreateMedidaSet();
  const deleteSet = useDeleteMedidaSet();
  const deleteMedida = useDeleteMedida();

  const [deleteTarget, setDeleteTarget] = useState<MedidaSetDTO | null>(null);
  const [showDeleteOrphans, setShowDeleteOrphans] = useState(false);

  const isLoading = setsLoading || orphanLoading;

  if (isLoading) {
    return <Card className="p-4"><SkeletonText lines={4} /></Card>;
  }

  const setsList = sets ?? [];
  const orphanList = orphanMedidas ?? [];
  const orphanCount = orphanList.length;
  const totalRegistros = setsList.length + (orphanCount > 0 ? 1 : 0);

  // If orphan medidas exist → navigate to them (they are the "current" registration)
  // If no orphans → auto-create a new named set and navigate to it
  const handleRegistrar = async () => {
    if (orphanCount > 0) {
      navigate(`/proyectos/${projectId}/medidas`);
      return;
    }
    const now = new Date();
    const dateStr = now.toLocaleDateString('es-EC', { day: 'numeric', month: 'short', year: 'numeric' });
    let nombre = `Registro — ${dateStr}`;
    let counter = 1;
    while (setsList.some((s) => s.nombre === nombre)) {
      counter++;
      nombre = `Registro ${counter} — ${dateStr}`;
    }
    try {
      const created = await createSet.mutateAsync({ projectId, nombre, silent: true });
      navigate(`/proyectos/${projectId}/medidas?setId=${created.id}`);
    } catch { /* hook handles toast */ }
  };

  // Always create a new named set with sequential number
  const handleNuevoRegistro = async () => {
    let numero = setsList.length + 1;
    while (setsList.some((s) => s.nombre === `Registro de Medidas ${numero}`)) {
      numero++;
    }
    const nombre = `Registro de Medidas ${numero}`;
    try {
      const created = await createSet.mutateAsync({ projectId, nombre, silent: true });
      navigate(`/proyectos/${projectId}/medidas?setId=${created.id}`);
    } catch { /* hook handles toast */ }
  };

  const handleDeleteSet = async () => {
    if (!deleteTarget) return;
    const nombre = deleteTarget.nombre;
    try {
      await deleteSet.mutateAsync({ projectId, setId: deleteTarget.id, silent: true });
      toast('success', `Registro "${nombre}" eliminado`);
      queryClient.invalidateQueries({ queryKey: queryKeys.projectSummary.detail(projectId) });
      setDeleteTarget(null);
    } catch { /* hook handles error toast */ }
  };

  const handleDeleteOrphans = async () => {
    const count = orphanList.length;
    for (const m of orphanList) {
      try {
        await deleteMedida.mutateAsync({ medidaId: m.id, projectId, silent: true });
      } catch { /* continue */ }
    }
    toast('success', `${count} medida${count !== 1 ? 's' : ''} eliminada${count !== 1 ? 's' : ''}`);
    queryClient.invalidateQueries({ queryKey: queryKeys.projectSummary.detail(projectId) });
    setShowDeleteOrphans(false);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {totalRegistros === 0 ? 'Sin registros de medidas' : `${totalRegistros} registro(s) de medidas`}
        </p>
        <div className="flex gap-2">
          <Button variant="primary" size="sm" onClick={handleNuevoRegistro} isLoading={createSet.isPending}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            Nuevo Registro
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {totalRegistros === 0 && (
        <Card className="p-6 text-center">
          <Ruler className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-3">
            Registra las medidas del proyecto. Cada registro es independiente y puede generar su propia proforma.
          </p>
          <Button variant="primary" size="sm" onClick={handleNuevoRegistro} isLoading={createSet.isPending}>
            Nuevo Registro
          </Button>
        </Card>
      )}

      {/* Orphan medidas section */}
      {orphanCount > 0 && (
        <Card className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-brand-dark">Medidas sin registro</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {orphanCount} medida(s) · registradas directamente
              </p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/proyectos/${projectId}/medidas`)}
              >
                Ver / Editar
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowDeleteOrphans(true)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Named registrations list */}
      {setsList.map((s) => (
        <Card key={s.id} className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-brand-dark">{s.nombre}</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {s.medidas_count} medida(s) · {new Date(s.created_at).toLocaleDateString('es-EC')}
                {s.proformas_count > 0 && ` · ${s.proformas_count} proforma(s)`}
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5 shrink-0">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/proyectos/${projectId}/medidas?setId=${s.id}`)}
              >
                Ver / Editar
              </Button>
              {s.medidas_count > 0 && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/proyectos/${projectId}/nueva-proforma?setId=${s.id}`)}
                >
                  + Proforma
                </Button>
              )}
              {s.proformas_count === 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setDeleteTarget(s)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        </Card>
      ))}

      {/* Delete named set modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar registro de medidas"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            ¿Eliminar el registro <strong>{deleteTarget?.nombre}</strong> y sus {deleteTarget?.medidas_count} medida(s)?
            Esta acción no se puede deshacer.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="danger" size="sm" onClick={handleDeleteSet} disabled={deleteSet.isPending}>
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete orphan medidas modal */}
      <Modal
        isOpen={showDeleteOrphans}
        onClose={() => setShowDeleteOrphans(false)}
        title="Eliminar medidas sin registro"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            ¿Eliminar las <strong>{orphanCount} medida(s)</strong> registradas directamente?
            Esta acción no se puede deshacer.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowDeleteOrphans(false)}>Cancelar</Button>
            <Button variant="danger" size="sm" onClick={handleDeleteOrphans} disabled={deleteMedida.isPending}>
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Maestros Tab (C-19) ─────────────────────────────────────────────

function MaestrosTab({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const { data: maestros, isLoading } = useProjectMaestros(projectId);
  const syncMutation = useSyncMaestroRates();
  const [showAssign, setShowAssign] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<AssignedMaestroDTO | null>(null);
  const [rateTarget, setRateTarget] = useState<{ maestro: AssignedMaestroDTO; preselectedRecipeId?: string } | null>(null);

  const hasMaestros = (maestros?.length ?? 0) > 0;

  return (
    <div className="space-y-4">
      {/* Advisory message */}
      {!hasMaestros && !isLoading && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <p className="text-sm">
            Aún no hay maestros asignados a este proyecto.
          </p>
        </div>
      )}

      {/* Header with assign button */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-brand-dark">
          Maestros asignados
          {maestros && <span className="text-gray-400 ml-2">({maestros.length})</span>}
        </h2>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => syncMutation.mutate(projectId)}
              disabled={syncMutation.isPending}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              Sincronizar APUs
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowAssign(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Asignar maestro
            </Button>
          </div>
        )}
      </div>

      {/* Loading */}
      {isLoading && <SkeletonTable rows={3} cols={4} />}

      {/* Empty state */}
      {!isLoading && !hasMaestros && (
        <Card>
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No hay maestros asignados a este proyecto</p>
          </div>
        </Card>
      )}

      {/* Maestro cards */}
      {!isLoading && maestros?.map((m) => (
        <MaestroCard
          key={m.assignment_id}
          maestro={m}
          projectId={projectId}
          canEdit={canEdit}
          onRemove={() => setRemoveTarget(m)}
          onEditRate={() => setRateTarget({ maestro: m })}
          onEditExistingRate={(r) => setRateTarget({ maestro: m, preselectedRecipeId: r.recipe_id })}
        />
      ))}

      {/* Assign modal */}
      {showAssign && (
        <AssignMaestroModal
          projectId={projectId}
          assignedIds={maestros?.map((m) => m.maestro_id) ?? []}
          onClose={() => setShowAssign(false)}
        />
      )}

      {/* Remove confirmation */}
      {removeTarget && (
        <RemoveMaestroModal
          projectId={projectId}
          maestro={removeTarget}
          onClose={() => setRemoveTarget(null)}
        />
      )}

      {/* Rate editing */}
      {rateTarget && (
        <SetProjectRateModal
          projectId={projectId}
          maestro={rateTarget.maestro}
          preselectedRecipeId={rateTarget.preselectedRecipeId}
          onClose={() => setRateTarget(null)}
        />
      )}
    </div>
  );
}

// ── Maestro Card ────────────────────────────────────────────────────

function MaestroCard({
  maestro,
  projectId,
  canEdit,
  onRemove,
  onEditRate,
  onEditExistingRate,
}: {
  maestro: AssignedMaestroDTO;
  projectId: string;
  canEdit: boolean;
  onRemove: () => void;
  onEditRate: () => void;
  onEditExistingRate: (rate: AssignedMaestroDTO['project_rates'][number]) => void;
}) {
  const deleteMutation = useDeleteProjectRate();
  const [expandedRates, setExpandedRates] = useState<Set<string>>(new Set());

  const toggleRate = (rateId: string) => {
    setExpandedRates((prev) => {
      const next = new Set(prev);
      next.has(rateId) ? next.delete(rateId) : next.add(rateId);
      return next;
    });
  };

  return (
    <Card>
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-brand-dark">{maestro.nombre}</h3>
            {maestro.cedula && (
              <p className="text-xs text-gray-400">CI: {maestro.cedula}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <>
                <Button variant="ghost" size="sm" onClick={onEditRate}>
                  + Tarifa
                </Button>
                <Button variant="ghost" size="sm" onClick={onRemove} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4 mr-1" />
                  Quitar
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Especialidades */}
        <div className="flex flex-wrap gap-1.5">
          {maestro.especialidades.map((esp) => (
            <Badge key={esp} color="blue">{esp}</Badge>
          ))}
        </div>

        {/* Rates table */}
        {maestro.project_rates.length > 0 && (
          <div className="mt-2">
            <p className="text-xs font-medium text-gray-500 mb-1">Tarifas efectivas:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-100">
                    <th className="py-1 pr-2 w-4" />
                    <th className="py-1 pr-4">APU</th>
                    <th className="py-1 pr-4 text-right">Precio</th>
                    <th className="py-1 pr-4">Unidad</th>
                    {canEdit && <th className="py-1" />}
                  </tr>
                </thead>
                <tbody>
                  {maestro.project_rates.map((r) => {
                    const hasMdo = r.mdo_items && r.mdo_items.length > 0;
                    const isExpanded = expandedRates.has(r.rate_id);
                    return (
                      <>
                        <tr key={r.rate_id} className="border-b border-gray-50">
                          <td className="py-1 pr-2">
                            {hasMdo && (
                              <button
                                onClick={() => toggleRate(r.rate_id)}
                                className="text-gray-400 hover:text-gray-600"
                                title={isExpanded ? 'Ocultar MDO' : 'Ver detalle MDO'}
                              >
                                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                              </button>
                            )}
                          </td>
                          <td className="py-1 pr-4 text-gray-700">{r.recipe_nombre}</td>
                          <td className="py-1 pr-4 text-right font-medium">
                            ${r.precio_unitario.toFixed(2)}
                          </td>
                          <td className="py-1 pr-4 text-gray-500">{r.unidad}</td>
                          {canEdit && (
                            <td className="py-1">
                              <div className="flex items-center gap-1 justify-end">
                                <button
                                  onClick={() => onEditExistingRate(r)}
                                  className="text-blue-500 hover:text-blue-700 p-0.5"
                                  title="Editar tarifa"
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => deleteMutation.mutate({ projectId, maestroId: maestro.maestro_id, rateId: r.rate_id })}
                                  disabled={deleteMutation.isPending}
                                  className="text-red-400 hover:text-red-600 p-0.5"
                                  title="Eliminar tarifa"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                        {hasMdo && isExpanded && r.mdo_items.map((mdo) => (
                          <tr key={mdo.material_id} className="bg-blue-50/40">
                            <td className="py-0.5 pr-2" />
                            <td className="py-0.5 pr-4 pl-4 text-blue-700 italic">{mdo.material_nombre}</td>
                            <td className="py-0.5 pr-4 text-right text-blue-600">${mdo.precio_unitario.toFixed(2)}</td>
                            <td className="py-0.5 pr-4 text-blue-400 text-xs">{r.unidad}</td>
                            {canEdit && <td />}
                          </tr>
                        ))}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {maestro.project_rates.length === 0 && (
          <p className="text-xs text-gray-400 italic">Sin tarifas configuradas</p>
        )}
      </div>
    </Card>
  );
}

// ── Assign Maestro Modal ────────────────────────────────────────────

function AssignMaestroModal({
  projectId,
  assignedIds,
  onClose,
}: {
  projectId: string;
  assignedIds: string[];
  onClose: () => void;
}) {
  const { data: allMaestros } = useMaestros({ isActive: 'true', limit: 100 });
  const assignMutation = useAssignMaestro();
  const [selectedMaestroId, setSelectedMaestroId] = useState('');

  const available = useMemo(() => {
    if (!allMaestros?.data) return [];
    return allMaestros.data.filter((m: MaestroListDTO) => !assignedIds.includes(m.id));
  }, [allMaestros, assignedIds]);

  const handleAssign = async () => {
    if (!selectedMaestroId) return;
    await assignMutation.mutateAsync({ projectId, data: { maestroId: selectedMaestroId } });
    onClose();
  };

  return (
    <Modal isOpen title="Asignar maestro al proyecto" onClose={onClose}>
      <div className="space-y-4">
        {available.length === 0 ? (
          <p className="text-sm text-gray-500">No hay maestros disponibles para asignar</p>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Seleccionar maestro
            </label>
            <select
              value={selectedMaestroId}
              onChange={(e) => setSelectedMaestroId(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#3c98ff] focus:outline-none focus:ring-1 focus:ring-[#3c98ff]"
            >
              <option value="">-- Seleccionar --</option>
              {available.map((m: MaestroListDTO) => (
                <option key={m.id} value={m.id}>
                  {m.nombre} {m.cedula ? `(${m.cedula})` : ''} — {m.especialidades.join(', ')}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAssign}
            isLoading={assignMutation.isPending}
            disabled={!selectedMaestroId}
          >
            Asignar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Remove Maestro Modal ────────────────────────────────────────────

function RemoveMaestroModal({
  projectId,
  maestro,
  onClose,
}: {
  projectId: string;
  maestro: AssignedMaestroDTO;
  onClose: () => void;
}) {
  const removeMutation = useRemoveMaestro();

  const handleRemove = async () => {
    await removeMutation.mutateAsync({ projectId, maestroId: maestro.maestro_id });
    onClose();
  };

  return (
    <Modal isOpen title="Quitar maestro del proyecto" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          ¿Deseas quitar a <span className="font-semibold">{maestro.nombre}</span> de este proyecto?
        </p>

        {maestro.project_rates.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p className="text-xs">
              Este maestro tiene {maestro.project_rates.length} tarifa(s) configurada(s) en este proyecto.
              Las tarifas de proyecto se conservaran por si se reasigna en el futuro.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleRemove}
            isLoading={removeMutation.isPending}
          >
            Quitar maestro
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Set Project Rate Modal ──────────────────────────────────────────

function SetProjectRateModal({
  projectId,
  maestro,
  preselectedRecipeId,
  onClose,
}: {
  projectId: string;
  maestro: AssignedMaestroDTO;
  preselectedRecipeId?: string;
  onClose: () => void;
}) {
  const { data: allRecipes } = useRecipes({ limit: 100 });
  const setRateMutation = useSetProjectRate();
  const [selectedRecipeId, setSelectedRecipeId] = useState(preselectedRecipeId ?? '');
  const [precio, setPrecio] = useState('');
  // Editable MDO prices: materialId → price string (per maestro per project)
  const [mdoPrices, setMdoPrices] = useState<Record<string, string>>({});
  const { data: recipeDetail } = useRecipe(selectedRecipeId);

  const existingRate = maestro.project_rates.find((r) => r.recipe_id === selectedRecipeId);

  // Initialize MDO prices from saved project-rate items (priority) or recipe catalog
  useEffect(() => {
    setPrecio('');
    if (!selectedRecipeId) { setMdoPrices({}); return; }
    if (existingRate && existingRate.mdo_items.length > 0) {
      const prices: Record<string, string> = {};
      for (const item of existingRate.mdo_items) prices[item.material_id] = String(item.precio_unitario);
      setMdoPrices(prices);
      return;
    }
    // Will be filled by recipeDetail effect when it loads
    setMdoPrices({});
  }, [selectedRecipeId]);

  useEffect(() => {
    if (!recipeDetail || !selectedRecipeId) return;
    if (existingRate && existingRate.mdo_items.length > 0) return; // already initialized from saved items
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mdoFromRecipe = (recipeDetail.materials as any[]).filter((m) => m.is_mdo);
    if (mdoFromRecipe.length === 0) return;
    const prices: Record<string, string> = {};
    for (const m of mdoFromRecipe) {
      prices[m.material_id] = (m.material_precio_unitario * m.cantidad_por_unidad).toFixed(2);
    }
    setMdoPrices(prices);
  }, [recipeDetail]);

  const recipeOptions = useMemo(() => {
    if (!allRecipes?.data) return [];
    return allRecipes.data.map((r: RecipeListDTO) => ({
      id: r.id,
      label: `${r.codigo} — ${r.nombre}`,
      unidad: r.unidad,
    }));
  }, [allRecipes]);

  // Build the display list for editable MDO items
  const editableMdoItems = useMemo(() => {
    if (existingRate && existingRate.mdo_items.length > 0) {
      return existingRate.mdo_items.map((item) => ({
        material_id: item.material_id,
        nombre: item.material_nombre,
      }));
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((recipeDetail?.materials as any[] | undefined) ?? [])
      .filter((m) => m.is_mdo)
      .map((m) => ({ material_id: m.material_id, nombre: m.material_nombre }));
  }, [existingRate, recipeDetail]);

  // Total is always derived from the editable MDO prices
  const mdoTotal = useMemo(
    () => Object.values(mdoPrices).reduce((sum, v) => sum + (parseFloat(v) || 0), 0),
    [mdoPrices],
  );

  // Price field: manual override > MDO total > existing rate
  const displayPrecio = precio !== ''
    ? precio
    : (mdoTotal > 0 ? mdoTotal.toFixed(2) : (existingRate ? String(existingRate.precio_unitario) : ''));

  const handleSave = async () => {
    if (!selectedRecipeId || !displayPrecio) return;
    await setRateMutation.mutateAsync({
      projectId,
      maestroId: maestro.maestro_id,
      data: {
        recipeId: selectedRecipeId,
        precioUnitario: Number(displayPrecio),
        mdoItems: editableMdoItems.map((mdo) => ({
          materialId: mdo.material_id,
          precioUnitario: parseFloat(mdoPrices[mdo.material_id] ?? '0') || 0,
        })),
      },
    });
    onClose();
  };

  return (
    <Modal isOpen title={`Tarifa de proyecto — ${maestro.nombre}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">APU</label>
          <select
            value={selectedRecipeId}
            onChange={(e) => setSelectedRecipeId(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#3c98ff] focus:outline-none focus:ring-1 focus:ring-[#3c98ff]"
          >
            <option value="">-- Seleccionar APU --</option>
            {recipeOptions.map((r: { id: string; label: string; unidad: string }) => (
              <option key={r.id} value={r.id}>
                {r.label} ({r.unidad})
              </option>
            ))}
          </select>
        </div>

        {/* Editable MDO items breakdown */}
        {selectedRecipeId && editableMdoItems.length > 0 && (
          <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
            <p className="text-xs font-medium text-blue-700 mb-2">Mano de Obra (editable por maestro):</p>
            <div className="space-y-2">
              {editableMdoItems.map((mdo) => (
                <div key={mdo.material_id} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-700 flex-1">{mdo.nombre}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={mdoPrices[mdo.material_id] ?? ''}
                      onChange={(e) => setMdoPrices((prev) => ({ ...prev, [mdo.material_id]: e.target.value }))}
                      className="w-24 rounded border border-gray-200 bg-white px-2 py-1 text-sm text-right focus:border-[#3c98ff] focus:outline-none"
                    />
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between text-sm font-semibold border-t border-blue-200 pt-1.5 mt-1">
                <span className="text-blue-800">Total MDO</span>
                <span className="text-blue-800">${mdoTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Precio unitario (proyecto)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={displayPrecio}
            onChange={(e) => setPrecio(e.target.value)}
            placeholder="0.00"
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#3c98ff] focus:outline-none focus:ring-1 focus:ring-[#3c98ff]"
          />
          {editableMdoItems.length > 0 && (
            <p className="text-xs text-gray-400 mt-1">Se calcula automáticamente del total MDO. Puedes ajustarlo manualmente.</p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            isLoading={setRateMutation.isPending}
            disabled={!selectedRecipeId || !displayPrecio}
          >
            Guardar tarifa
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Liquidacion Tab ────────────────────────────────────────────────

const LIQUIDACION_ELIGIBLE = ['en_ejecucion', 'pre_liquidacion', 'liquidado', 'finalizado'];

function LiquidacionTab({
  projectId,
  projectEstado,
  navigate,
}: {
  projectId: string;
  projectEstado: string;
  navigate: (path: string) => void;
}) {
  if (!LIQUIDACION_ELIGIBLE.includes(projectEstado)) {
    return (
      <Card>
        <div className="py-8 text-center space-y-2">
          <DollarSign className="w-10 h-10 text-gray-300 mx-auto" />
          <p className="text-sm text-gray-500">
            La liquidacion no esta disponible para este proyecto.
          </p>
          <p className="text-xs text-gray-400">
            El proyecto debe completar su ejecucion antes de liquidarse.
          </p>
        </div>
      </Card>
    );
  }

  const isEnEjecucion = projectEstado === 'en_ejecucion';

  return (
    <Card>
      <div className="py-8 text-center space-y-4">
        <DollarSign className="w-10 h-10 text-[#3c98ff] mx-auto" />
        <div>
          <p className="text-sm font-medium text-brand-dark">
            {isEnEjecucion ? 'Iniciar proceso de liquidacion' : 'Modulo de liquidacion'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {isEnEjecucion
              ? 'Crea la pre-liquidacion para comenzar el cierre financiero del proyecto.'
              : 'Accede al modulo completo de liquidacion para este proyecto.'}
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate(`/liquidacion?projectId=${projectId}`)}
        >
          {isEnEjecucion ? 'Crear Pre-Liquidacion' : 'Ir a Liquidacion'}
        </Button>
      </div>
    </Card>
  );
}
